import { useMemo } from 'react';
import useSWR from 'swr';

import { endpoints, fetcher } from 'src/utils/axios';
import {
  buildCompetencyHierarchy,
  CompetencyHierarchy,
  type CompetencyHierarchyNode,
} from 'src/utils/competency-hierarchy';

type CompetencyHierarchyResponse = {
  nodes: CompetencyHierarchyNode[];
};

type CompetencyHierarchyExpansion = 'ancestors' | 'descendants';

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

function buildHierarchyUrl(
  competencyIds: number[],
  expand: CompetencyHierarchyExpansion,
): string | null {
  if (competencyIds.length === 0) {
    return null;
  }

  const uniqueIds = Array.from(new Set(competencyIds)).sort((a, b) => a - b);
  const params = new URLSearchParams({
    competencyIds: uniqueIds.join(','),
    expand,
  });
  return `${endpoints.competency.hierarchy}?${params.toString()}`;
}

export function useCompetencyHierarchy(
  competencyIds: number[],
  expand: CompetencyHierarchyExpansion = 'ancestors',
): {
  competencyHierarchy: CompetencyHierarchy;
  loading: boolean;
} {
  const sortedUniqueIds = useMemo(
    () =>
      Array.from(new Set(competencyIds.filter((id) => Number.isFinite(id)))).sort(
        (a, b) => a - b,
      ),
    [competencyIds],
  );

  const hierarchyUrl = useMemo(
    () => buildHierarchyUrl(sortedUniqueIds, expand),
    [expand, sortedUniqueIds],
  );

  const { data, isLoading } = useSWR<CompetencyHierarchyResponse>(
    hierarchyUrl,
    fetcher,
    swrOptions,
  );

  const competencyHierarchy = useMemo(
    () => buildCompetencyHierarchy(data?.nodes ?? []),
    [data?.nodes],
  );

  return {
    competencyHierarchy,
    loading: hierarchyUrl != null && isLoading,
  };
}

/** Ancestor-expanded subgraph for activity competency coverage validation. */
export function useActivityCompetencyValidationHierarchy(competencyIds: number[]): {
  competencyHierarchy: CompetencyHierarchy;
  loading: boolean;
} {
  return useCompetencyHierarchy(competencyIds, 'ancestors');
}
